import { InvoiceEvent } from '@/domain/models/invoice/internal-company-manager/invoice-event-model'
import { Pagination } from '@/domain/models/pagination-model'
import { Account } from '@/domain/usecases'
import { InvoiceEventSearch } from '@/domain/usecases/invoice/internal-company-manager/invoice-event-search'
import { parseNumber } from '@/main/helpers/number-helper'
import { QueryClient } from 'react-query'
import { LoaderFunctionArgs } from 'react-router-dom'
import InternalCompanyManagerBaseLoader from '../internal-company-manager/internal-company-manager-base-loader'

export default class InvoiceListLoader extends InternalCompanyManagerBaseLoader {
  constructor(
    readonly account: Account.Model,
    readonly queryClient: QueryClient
  ) {
    super(account, queryClient)
  }

  async loader({ request }: LoaderFunctionArgs) {
    const url = new URL(request.url)
    const params = this.createSearchParams(url.searchParams)
    const tab = this.parseStatusGroup(url.searchParams.get(InvoiceEventSearch.TAB_QUERY_PARAM_NAME))

    return this.defer({
      data: import('../../../factories/usecases')
        .then(async module => {
          return module.makeRemoteInvoiceEventSearch
        })
        .then(async makeRemoteInvoiceEventSearch => {
          const remoteInvoiceEventSearch = makeRemoteInvoiceEventSearch(this.queryClient)
          return {
            data: remoteInvoiceEventSearch.loader(params),
            remote: remoteInvoiceEventSearch,
            params,
            tab
          }
        })
    })
  }

  protected createSearchParams(searchParams: URLSearchParams): InvoiceEventSearch.Params {
    const tab = searchParams.get(InvoiceEventSearch.TAB_QUERY_PARAM_NAME)
    const search = searchParams.get(InvoiceEventSearch.SEARCH_QUERY_PARAM_NAME)
    const page = searchParams.get(Pagination.PAGE_PARAM_NAME)
    const pageSize = searchParams.get(Pagination.PAGE_SIZE_PARAM_NAME)

    return {
      tab: tab ? this.parseStatusGroup(tab) : InvoiceEventSearch.TabListGroup.NOT_CLASSIFIED,
      search: search ?? '',
      statuses: this.parseStatus(tab),
      page: parseNumber(page, Pagination.DEFAULT_PAGE)!,
      pageSize: parseNumber(pageSize, InvoiceEventSearch.DEFAULT_PAGE_SIZE)
    }
  }

  parseStatusGroup(tab: string | null): InvoiceEventSearch.TabListGroup {
    if (!tab) return InvoiceEventSearch.TabListGroup.NOT_CLASSIFIED
    return (
      Object.values(InvoiceEventSearch.TabListGroup).find(
        s => s.toLowerCase() === tab.toLowerCase()
      ) ?? InvoiceEventSearch.TabListGroup.NOT_CLASSIFIED
    )
  }

  parseStatus(tab: string | null): InvoiceEvent.Status[] {
    return InvoiceEventSearch.parseInvoiceEventStatus(this.parseStatusGroup(tab))
  }
}
